import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  concat,
  Subject,
  Subscription,
  throwError,
  combineLatest,
  from,
} from 'rxjs';
import {
  distinctUntilChanged,
  tap,
  switchMap,
  catchError,
  map,
  takeUntil,
  debounceTime,
  filter,
} from 'rxjs/operators';

import { ResultData } from '@app/core/_common/model';
import { ProductStatus } from '@app/core/_constant/product.constants';
import { LocationService } from '@app/core/locations/services/location.service';
import { Location } from '@app/core/locations/store/locations.model';
import { PurchaseOrderShipmentService } from '@app/core/orders/services/purchase-order-shipment.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { RfqLotService } from '@app/core/rfq-lots/services/rfq-lots.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { IRfqLot } from 'v2/modules/rfq/interfaces/rfq-lot.interface';
import { IWarehouse } from 'v2/modules/shared/interfaces/location-warehouse.interface';

import { BulkTagsComponent } from '../../bulk-tags/bulk-tags.component';
import { RfqLotFormComponent } from '../rfq-lot-form/rfq-lot-form.component';

enum ReceivingReportItemSource {
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  SHIPMENT = 'SHIPMENT',
}

enum DefaultShipToAddress {
  WAREHOUSE = 'warehouse',
  SHIP_TO_ADDRESS = 'ship_to_address',
}

@Component({
  selector: 'cnc-receiving-report-form-rfqs',
  templateUrl: './receiving-report-form-rfqs.component.html',
  styleUrls: ['./receiving-report-form-rfqs.component.scss'],
})
export class ReceivingReportFormRfqsComponent implements OnInit {
  @Output()
  updateLocations = new EventEmitter<any>();

  @Input()
  itemLocationsEnabled :boolean;

  url :string = environment.url;

  loading :boolean = false;

  po_number :any[] = [];
  pageTitle :string = 'Add RR Items/SKUs';

  rrItems :any[] = [];

  purchaseOrdersList :any[] = [];
  selectedPurchaseOrders :any[] = [];

  purchaseOrder$ :Observable<any[]>;
  purchaseOrderLoading = false;
  purchaseOrderInput$ = new Subject<string>();

  @Input()
  warehouses :any[] = [];
  locations :any[] = [];

  checked :any = [];
  checkedAll :boolean = false;
  checkedCount :number = 0;

  unitOfMeasures :any[] = [];

  tags :any[] = [];
  tagsPoData :any[];

  shipments$ :Observable<any[]>;
  shipmentsLoading = false;
  shipmentsInput$ = new Subject<string>();

  locationInput$ = new Subject<string>();
  locations$ :Observable<any>;

  rrItemSource = ReceivingReportItemSource;

  productStatus = ProductStatus;

  unsubscribeTags :Subscription;
  private onDestroy$ = new Subject();
  rfqLots :any[] = [];

  @Input()
  ftLocationBins :boolean = false;

  @Input()
  ftLotTracking :boolean = false;

  @Input()
  pickingWarehouseId :number = 0;

  loadedWarehouseIds :number[] = [];
  relations = [
    'factory',
    'location',
    'warehouse.defaultReceivingLocation',
    'purchaseOrderItems.orderedUnitOfMeasure',
    'purchaseOrderItems.rfq.defaultReceivingBin.parent',
    'purchaseOrderItems.lot',
    'internalResource',
  ];
  exactPurchaseOrders$ :Observable<any[]>;
  similarPurchaseOrders$ :Observable<any[]>;

  featureFlag = EFeatureFlag;

  constructor(
    public activeModal :NgbActiveModal,
    private poSvc :PoService,
    public locationSvc :LocationService,
    private tagSvc :ProductTagService,
    private cd :ChangeDetectorRef,
    private modalSvc :NgbModal,
    private shipmentService :PurchaseOrderShipmentService,
    public rfqLotSvc :RfqLotService,
    private toastr :ToastrService,
  ) {}

  ngOnInit() {
    this.loadShipments();
    this.loadTags();
    this.loadDataFromService();
    this.onSearchUpdate().subscribe();
    this.getSupplierPurchaseOrders().subscribe();
  }

  ngOnDestroy() :void {
    if (this.unsubscribeTags) {
      this.unsubscribeTags.unsubscribe();
    }
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  duplicateRow(item, idx) {
    this.rrItems.splice(idx, 0, Object.assign({}, item));
  }

  private loadTags() :void {
    this.tagSvc.getTags({ parent_only: true }, null);
    this.unsubscribeTags = this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });
  }

  checkAll() :void {
    if (!this.checkedAll) {
      this.checked = [];
      this.countChecked();
      this.cd.detectChanges();

      return;
    }
    this.rrItems.forEach((r, idx) => {
      this.checked[idx] = this.checkedAll;
    });
    this.countChecked();
  }

  addBulkTags() :void {
    const modalRef = this.modalSvc.open(BulkTagsComponent, {
      size: 'md',
      windowClass: 'xl1024',
    });
    modalRef.result.then(
      (resp) => {
        this.checked.forEach((data, index) => {
          this.rrItems[index].tag_ids = resp;
        });
        this.checkedAll = false;
        this.checkAll();
      },
      (error) => {},
    );
  }

  countChecked() :void {
    this.checkedCount = this.checked.filter((x) => {
      return x;
    }).length;
  }

  customTagsSearchFn(term :any, item :any) :void {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  onRemove(event :any, source :ReceivingReportItemSource) :void {
    const items :string[] = this.rrItems;
    const selectedItemId :number = parseInt(event.value.id);
    this.rrItems.forEach((rrItem, index) => {
      if (
        (source === ReceivingReportItemSource.PURCHASE_ORDER &&
          parseInt(rrItem.purchase_order_id) === selectedItemId) ||
        (source === ReceivingReportItemSource.SHIPMENT &&
          parseInt(rrItem.shipment_id) === selectedItemId)
      ) {
        items.splice(index, 1);
      }
    });
  }

  private getWarehouseInfo(warehouseId :number, idx :number) :Observable<any> {
    return this.locationSvc.getById(warehouseId).pipe(
      tap((warehouse :any) => {
        const rrItemLocationId :number = this.rrItems[idx].location_id;
        this.getDefaultReceivingLocation(
          warehouseId,
          rrItemLocationId === 0
            ? warehouse.default_receiving_location
            : rrItemLocationId,
          idx,
        ).subscribe();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getDefaultReceivingLocation(
    id :number,
    defaultReceivingLocationId :number,
    idx :number,
  ) :Observable<any> {
    return this.locationSvc.getAllLocations(id).pipe(
      tap((resp) => {
        const location = resp.find((row) => row.id == defaultReceivingLocationId);
        this.rrItems[idx].location_id = location?.id;
        this.locations = resp;
        this.rrItems[idx].loading = false;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  getPOItems(po :any) :void {
    po?.purchase_order_items?.forEach((item :any) => {
      const rrItem :any = this.rrItems.find(
        (i) =>
          i.purchase_order_rfq_id === item.id &&
          i.purchase_order_id === po.id &&
          i.source === ReceivingReportItemSource.PURCHASE_ORDER,
      );
      if (!rrItem) {
        if (item.received_entries?.quantity >= item.quantity) {
          return;
        }

        item.quantity = item.received_entries
          ? item.quantity - item.received_entries?.quantity
          : item.quantity;

        if (this.warehouses.length > 0) {
          item.warehouse_id = this.warehouses[0].id;
          item.location_id = 0; //this.locations[0].id;
        }
        item.purchase_order_rfq_id = item.id;
        delete item.id;

        if (item.rfq) {
          this.unitOfMeasures[item.rfq.id] = item.rfq.unit_of_measures;
          if (item.rfq.unit_of_measures.length <= 0) {
            this.unitOfMeasures[item.rfq.id] = item.rfq.parent.unit_of_measures;
          }
        }

        item.received_uom_id = item.ordered_uom_id;
        item.location = null;
        item.warehouse = null;
        if (
          this.itemLocationsEnabled &&
          po?.default_ship_to_address !== DefaultShipToAddress.WAREHOUSE &&
          item?.rfq?.default_receiving_bin
        ) {
          item.warehouse_id = item.rfq.default_receiving_bin.parent_id;
          item.location_id = item.rfq.default_receiving_bin.id;
          item.location = item.rfq?.default_receiving_bin;
          item.warehouse = item.rfq?.default_receiving_bin?.parent;
        }

        item.lot_id = 0;
        item.lot = null;
        item.lot_tracking = item.rfq.lot_tracking;

        if (this.itemLocationsEnabled && this.pickingWarehouseId > 0) {
          item.warehouse_id = this.pickingWarehouseId;
        }

        if (
          this.itemLocationsEnabled &&
          po?.default_ship_to_address === DefaultShipToAddress.WAREHOUSE &&
          po?.warehouse_id !== 0
        ) {
          item.warehouse_id = po?.warehouse_id;
          item.warehouse = po?.warehouse || null;
          item.location_id = po?.warehouse?.default_receiving_location?.id;
          item.location = po?.warehouse?.default_receiving_location;

          if (
            this.pickingWarehouseId ===
              item?.rfq?.default_receiving_bin?.parent_id &&
            po?.warehouse_id === item?.rfq?.default_receiving_bin?.parent_id
          ) {
            item.location_id = item?.rfq?.default_receiving_bin?.id;
            item.location = item?.rfq?.default_receiving_bin;
          }
        }

        // item
        item.source = ReceivingReportItemSource.PURCHASE_ORDER;
        item.manufacturer_code = po.factory?.factory_name
          ? po.factory?.factory_name
              .replace(/\s/g, '')
              .toUpperCase()
              .substring(0, 3)
          : '';
        this.rrItems.push(item);
        const rrItemIdx = this.rrItems.findIndex(
          (rrItem) => rrItem.rfq_id === item.rfq_id,
        );
        this.computeLineTotal(rrItemIdx);

        // if (this.itemLocationsEnabled) {
        //   const idx = this.rrItems.length - 1;
        //   this.loadWarehouseLocations(idx);
        // }

        if (item?.rfq?.status === this.productStatus.DELETED) {
          this.toastr.warning(`SKU ${item.internal_sku} is deleted`);
        }
      }
    });
    // if (this.rrItems.length > 0) {
    //   const rfqIds = this.rrItems.map((item) => item.rfq_id);
    //   if (this.checkIfRfqLotExists(rfqIds)) {
    //     this.getRfqLots(rfqIds);
    //   }
    // }

    this.cd.detectChanges();
  }

  private loadpurchaseOrder() {
    this.purchaseOrderLoading = false;
    this.purchaseOrder$ = concat(
      this.purchaseOrderInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.purchaseOrderLoading = true)),
        switchMap((term) =>
          this.poSvc.getByPONumber(term, true, this.relations.join(',')).pipe(
            catchError((response) => {
              this.purchaseOrderLoading = false;

              return throwError(response.error);
            }),
            takeUntil(this.onDestroy$),
            // tap(() => (this.purchaseOrderLoading = false))
            tap((resp) => {
              if (this.checkTerm(term)) {
                this.purchaseOrdersList = resp.data;
              } else {
                this.purchaseOrdersList = [];
              }
              this.purchaseOrderLoading = false;
            }),
            map((resp) => {
              this.purchaseOrderLoading = false;

              return this.checkTerm(term) ? resp.data : (resp.data = []);
            }),
          ),
        ),
      ),
    );
  }

  private checkTerm(term :string) :boolean {
    return !term || term === '' ? false : true;
  }

  addItems() {
    this.activeModal.close({
      rrItems: this.rrItems,
      unitOfMeasures: this.unitOfMeasures,
    });
  }

  computeLineTotal(idx :number) :void {
    if (idx > -1) {
      this.rrItems[idx].line_total =
        this.rrItems[idx].quantity * this.rrItems[idx].unit_price;
    }
  }

  loadWarehouseLocations(idx :number) :void {
    const item = this.rrItems[idx];
    const warehouseId =
      typeof item?.warehouse_id === 'number'
        ? item?.warehouse_id
        : parseInt(item?.warehouse_id);

    if (!warehouseId) return;

    const locationsLoaded = this.locations.some(
      (loc) => loc.parent_id === warehouseId,
    );

    const loadedWarehouse = this.loadedWarehouseIds.includes(warehouseId);
    if (!locationsLoaded && !loadedWarehouse) {
      item.loading = true;
      this.getWarehouseInfo(warehouseId, idx).subscribe();
      this.loadedWarehouseIds.push(warehouseId);
    }

    const rrItemLocationId :number = this.rrItems[idx].location_id;
    if (loadedWarehouse) {
      const warehouseData = this.warehouses.find((warehouse) => {
        return warehouse.id === warehouseId;
      });
      this.getDefaultReceivingLocation(
        warehouseId,
        rrItemLocationId === 0
          ? warehouseData?.default_receiving_location
          : rrItemLocationId,
        idx,
      ).subscribe();
    }
    this.cd.detectChanges();
  }

  onWarehouseChange(idx :number) :void {
    this.rrItems[idx].location_id = null;
    if (!this.rrItems[idx].warehouse_id) return;
    this.loadWarehouseLocationsByWarehouseId(
      parseInt(this.rrItems[idx].warehouse_id),
      idx,
    );
  }

  loadWarehouseLocationsByWarehouseId(warehouseId :number, idx :number) :void {
    const hasLocations = this.locations.some(
      (location :Location) => location.parent_id === warehouseId,
    );
    if (hasLocations) return;
    this.rrItems[idx].loading = true;
    this.locationSvc.getAsync({ parent_id: warehouseId }).subscribe(
      (locationResponse :ResultData) => {
        this.locations = [...this.locations, ...locationResponse.data];
        this.rrItems[idx].loading = false;
      },
      (_) => {
        this.rrItems[idx].loading = false;
      },
    );
  }

  private loadDataFromService() :void {
    this.exactPurchaseOrders$ = this.poSvc.exactPurchaseOrders$;
    this.similarPurchaseOrders$ = this.poSvc.similarPurchaseOrders$;
  }

  getUomName(rfq_id :any, received_uom_id :any) :any {
    const result = Array.isArray(this.unitOfMeasures[rfq_id]);

    if (result) {
      return this.unitOfMeasures[rfq_id].find((x) => x.id === received_uom_id)
        ?.name;
    } else {
      return '';
    }
  }

  getShipmentItems(shipment :any) :void {
    if (!shipment) return;

    shipment = this.convertToLegacyFormat(shipment);

    shipment.items?.forEach((shipmentItem) => {
      const item = {
        ...shipmentItem.purchase_order_rfq,
        quantity: shipmentItem.quantity,
        ordered_qty: shipmentItem.ordered_qty,
        ordered_uom_id: shipmentItem.ordered_uom_id,
        base_uom_id: shipmentItem.base_uom_id,
        line_total: (
          shipmentItem.quantity * shipmentItem.purchase_order_rfq.unit_price
        ).toFixed(4),
      };

      item.shipment_id = shipment.id;

      const rrItem :any = this.rrItems.find(
        (i) =>
          i.shipment_id === shipment.id &&
          i.purchase_order_rfq_id === item.purchase_order_rfq_id &&
          i.source === ReceivingReportItemSource.SHIPMENT,
      );

      if (!rrItem) {
        if (this.warehouses.length > 0) {
          item.warehouse_id = this.warehouses[0].id;
          item.location_id = 0;
        }

        item.purchase_order_rfq_id = item.id;
        delete item.id;

        if (item.rfq?.unit_of_measures) {
          this.unitOfMeasures[item.rfq.id] = item.rfq.unit_of_measures;
        }

        if (this.itemLocationsEnabled && item?.rfq?.default_receiving_bin) {
          const { parent_id, id } = item.rfq.default_receiving_bin;
          item.warehouse_id = parent_id;
          item.location_id = id;
        }

        item.received_uom_id = item.ordered_uom_id;

        item.source = ReceivingReportItemSource.SHIPMENT;

        this.rrItems.push(item);

        // if (this.itemLocationsEnabled) {
        //   const idx = this.rrItems.length - 1;
        //   this.loadWarehouseLocations(idx);
        // }

        if (item?.rfq?.status === this.productStatus.DELETED) {
          this.toastr.warning(`SKU ${item.internal_sku} is deleted`);
        }
      }
    });

    // if (this.rrItems.length > 0) {
    //   const rfqIds = this.rrItems.map((item) => item.rfq_id);
    //   if (this.checkIfRfqLotExists(rfqIds)) {
    //     this.getRfqLots(rfqIds);
    //   }
    // }
  }

  clearItems(source :ReceivingReportItemSource) :void {
    this.rrItems = this.rrItems.filter((rrItem) => rrItem.source !== source);
  }

  private loadShipments() :void {
    this.shipmentsLoading = false;
    this.shipments$ = concat(
      this.shipmentsInput$.pipe(
        filter((term) => !!term),
        distinctUntilChanged(),
        debounceTime(800),
        tap(() => (this.shipmentsLoading = true)),
        takeUntil(this.onDestroy$),
        switchMap((term) =>
          this.shipmentService
            .search({
              searchQuery: term,
              relations: [
                'purchaseOrder',
                'purchaseOrder.factory',
                'items',
                'items.purchaseOrderRfq',
                'items.purchaseOrderRfq.rfq',
                'items.purchaseOrderRfq.rfq.unitOfMeasures',
                'items.purchaseOrderRfq.rfq.defaultReceivingBin.parent',
              ].join(','),
              limit: '20',
            })
            .pipe(
              map((resp) => {
                this.shipmentsLoading = false;

                return this.checkTerm(term) ? resp : [];
              }),
              takeUntil(this.onDestroy$),
              catchError((response) => {
                this.shipmentsLoading = false;

                return throwError(response.error);
              }),
            ),
        ),
      ),
    );
  }

  private convertToLegacyFormat(data :any) :any {
    const dataNewFormat :any = {};

    Object.keys(data).forEach((key) => {
      const keyToSnakeCase = key.replace(
        /[A-Z]/g,
        (letter) => `_${letter.toLowerCase()}`,
      );

      if (Array.isArray(data[key])) {
        // if array
        dataNewFormat[keyToSnakeCase] = data[key].map((e) =>
          this.convertToLegacyFormat(e),
        );
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        // if object
        dataNewFormat[keyToSnakeCase] = this.convertToLegacyFormat(data[key]);
      } else {
        // if string
        dataNewFormat[keyToSnakeCase] = data[key];
      }
    });

    return dataNewFormat;
  }

  createNewLot(rfq :any, idx :number = 0) :void {
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    modalRef.componentInstance.rfqId = rfq.rfq_id;
    modalRef.componentInstance.quantityOriginal = 0;
    modalRef.componentInstance.manufacturerCode = rfq.manufacturer_code;

    modalRef.result.then(
      (data) => {
        this.rfqLots = [...this.rfqLots, data];
        this.rrItems[idx].lot_id = data.id;
      },
      (_) => {},
    );
  }

  bulkCreateLot() :void {
    const rfqIds = this.selectedItems
      .filter((item) => item.lot_tracking)
      .map((item) => item.rfq_id);

    if (!rfqIds.length) {
      this.toastr.warning(`Please select atleast 1 SKU with lot tracking enabled.`);

      return;
    }

    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });

    modalRef.componentInstance.bulkUpdate = true;
    modalRef.componentInstance.rfqIds = rfqIds;
    modalRef.componentInstance.quantityOriginal = 0;

    modalRef.result.then(
      (newRfqLots) => {
        this.rfqLots = [...this.rfqLots, ...newRfqLots];

        this.checked.forEach((item, index) => {
          const lot = newRfqLots.find(
            (lot) => lot.rfq_id === this.rrItems[index]['rfq_id'],
          );
          this.rrItems[index]['lot_id'] = lot?.id || 0;
          this.rrItems[index]['lot'] = lot || null;
        });
      },
      (_) => {},
    );
  }

  private checkIfRfqLotExists(rfqIds :number[]) :boolean {
    return !this.rfqLots.some((rfqLot) =>
      rfqIds.some((rfqId) => rfqId === rfqLot.rfq_id),
    );
  }

  getRfqLots(rfqIds :number[]) :void {
    this.rfqLotSvc
      .getByRfqIds({
        rfqIds: rfqIds.join(','),
        limit: 1000,
      })
      .subscribe((resp) => {
        if (resp.data) {
          this.rfqLots = this.rfqLots.concat(resp.data);
        }
      });
  }

  enabledBinLocationAndLotTracking(rfq :any) :boolean {
    return rfq.lot_tracking && this.ftLocationBins && this.ftLotTracking;
  }

  private onSearchUpdate() :Observable<string> {
    return this.purchaseOrderInput$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((value :string) => {
        const exactQueryParams = {
          rrSearchPoKeyword: value,
          exact: true,
          relations: this.relations.join(','),
        };
        const similarQueryParams = { ...exactQueryParams, exact: false };
        this.loadSupplierPurchaseOrders(exactQueryParams);
        this.loadSupplierPurchaseOrders(similarQueryParams);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private loadSupplierPurchaseOrders(params :{
    [param :string] :string | string[] | boolean;
  }) :void {
    this.poSvc.getSupplierPurchaseOrders(params);
  }

  private getSupplierPurchaseOrders() :Observable<any[]> {
    return combineLatest(
      this.exactPurchaseOrders$,
      this.similarPurchaseOrders$,
    ).pipe(
      tap(([exactPurchaseOrders, similarPurchaseOrders]) => {
        const exactOrderIds = exactPurchaseOrders.map(
          (purchaseOrders) => purchaseOrders.id,
        );
        const formattedSimilarPurchaseOrders = similarPurchaseOrders.filter(
          (purchaseOrders) => {
            return exactOrderIds.indexOf(purchaseOrders.id) === -1;
          },
        );

        this.purchaseOrdersList = [
          ...exactPurchaseOrders,
          ...formattedSimilarPurchaseOrders,
        ];
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get selectedItems() :any[] {
    const selectedItems = [];

    this.checked.forEach((data, index) => {
      selectedItems.push(this.rrItems[index]);
    });

    return selectedItems;
  }

  onSelectLot(selectedRfqLot :IRfqLot, idx :number) :void {
    this.rrItems[idx]['lot_id'] = selectedRfqLot.id;
    this.rrItems[idx]['lot'] = selectedRfqLot;
  }

  onOpenCreateLotDialog(lineItem :any, idx :number) :void {
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    modalRef.componentInstance.rfqId = lineItem.rfq_id;
    modalRef.componentInstance.quantityOriginal = 0;
    modalRef.componentInstance.purchaseOrderId = lineItem.purchase_order_id;

    from(modalRef.result)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedRfqLot) => {
        this.onSelectLot(selectedRfqLot, idx);
      });
  }

  onSelectWarehouse(warehouse :IWarehouse, index :number) :void {
    this.rrItems[index] = {
      ...this.rrItems[index],
      warehouse: warehouse,
      warehouse_id: warehouse.id,
      location: null,
      location_id: 0,
    };
  }

  onSelectLocation(location :IWarehouse, index :number) :void {
    this.rrItems[index] = {
      ...this.rrItems[index],
      location: location,
      location_id: location.id,
    };
  }
}
